import { Transaction } from "@/types";

export const formatCZK = (value: number) =>
  value.toLocaleString("cs-CZ", {
    style: "currency",
    currency: "CZK",
    maximumFractionDigits: 0,
  });

export function normalizeAccessories(
  accessories: string[] | string | null | undefined,
): string[] {
  if (Array.isArray(accessories)) {
    return accessories
      .map((item) => item?.trim())
      .filter((item): item is string => Boolean(item));
  }

  if (typeof accessories === "string") {
    return accessories
      .split(/[;,]/)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
}

export function applyTransactionAmount(
  current: number,
  transactionType: string,
  amount: number,
) {
  if (transactionType === "INCOME") {
    return current + amount;
  }
  return current - amount;
}

export function buildAuthPayload(email: string, token: string, data?: any) {
  return {
    email,
    token,
    ...(data && { data }),
  };
}

export function median(values: number[]) {
  if (values.length === 0) return 0;

  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);

  if (sorted.length % 2 !== 0) {
    return sorted[mid];
  }

  return Math.round((sorted[mid - 1] + sorted[mid]) / 2);
}

export function mapOfferToChartItem<
  T extends { price: string; createdAt: string },
>(item: T) {
  const numericPrice = item.price.toString().replace(/[^\d.]/g, "");
  const priceNumber = Number(numericPrice);

  if (isNaN(priceNumber) || priceNumber <= 0) {
    return null;
  }

  return {
    date: item.createdAt.split("T")[0],
    price: priceNumber,
    offer: item,
  };
}

export function expandRecurringTransactions(
  transactions: Transaction[],
  selectedYear: number,
  today: Date,
) {
  const expandedTransactions: Transaction[] = [];
  const yearStart = new Date(`${selectedYear}-01-01T00:00:00.000Z`);
  const yearEnd = new Date(`${selectedYear}-12-31T23:59:59.999Z`);

  let newIdCounter = 1;

  transactions.forEach((t) => {
    const start = new Date(t.dateFrom);
    const end = t.dateTo ? new Date(t.dateTo) : yearEnd;

    switch (t.recurrence) {
      case "MONTHLY":
        let current = start > yearStart ? new Date(start) : new Date(yearStart);

        while (current <= end && current <= yearEnd) {
          expandedTransactions.push({
            ...t,
            id: newIdCounter++,
            originalId: Number(t.id),
            dateFrom: current.toISOString(),
            originalDateFrom: t.dateFrom,
            isPrediction: current > today,
          });

          current.setMonth(current.getMonth() + 1);
        }
        break;

      case "YEARLY":
        if (
          selectedYear >= start.getFullYear() &&
          selectedYear <= end.getFullYear()
        ) {
          const txnDate = new Date(
            Date.UTC(selectedYear, start.getMonth(), start.getDate()),
          );

          expandedTransactions.push({
            ...t,
            id: newIdCounter++,
            originalId: Number(t.id),
            dateFrom: txnDate.toISOString(),
            originalDateFrom: t.dateFrom,
            isPrediction: selectedYear > today.getFullYear(),
          });
        }
        break;

      case "ONCE":
        if (start <= yearEnd && start >= yearStart) {
          expandedTransactions.push({
            ...t,
            id: newIdCounter++,
            originalId: Number(t.id),
            dateFrom: t.dateFrom,
            originalDateFrom: t.dateFrom,
            isPrediction: false,
          });
        }
        break;
    }
  });

  return expandedTransactions;
}
